import { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';

import {
  EXTERNAL_PROVIDERS,
  providerLabel,
  type ExternalProvider,
  type ProviderRate,
  type ProviderRateInput,
  type ProviderRateUpdate,
} from '@/types/provider-cost';
import { COST_CURRENCIES, isDecimal, todayIso } from './format';

interface RateFormValues {
  provider: string;
  service: string;
  unitKey: string;
  costPerUnit: string;
  currency: string;
  effectiveFrom: string;
  effectiveTo: string;
  notes: string;
}

export interface ProviderRateModalProps {
  opened: boolean;
  onClose: () => void;
  /** The rate being corrected; absent to add a new one. */
  rate?: ProviderRate | null;
  onCreate: (input: ProviderRateInput) => Promise<unknown>;
  onUpdate: (id: string, update: ProviderRateUpdate) => Promise<unknown>;
}

function initialValues(rate: ProviderRate | null | undefined): RateFormValues {
  return rate
    ? {
        provider: rate.provider,
        service: rate.service,
        unitKey: rate.unitKey,
        costPerUnit: String(rate.costPerUnit),
        currency: rate.currency,
        effectiveFrom: rate.effectiveFrom,
        effectiveTo: rate.effectiveTo ?? '',
        notes: rate.notes ?? '',
      }
    : {
        provider: '',
        service: '',
        unitKey: '',
        costPerUnit: '',
        currency: 'GBP',
        effectiveFrom: todayIso(),
        effectiveTo: '',
        notes: '',
      };
}

/** The fields of a correction that differ from the rate as it stands. */
function changedFields(rate: ProviderRate, values: RateFormValues): ProviderRateUpdate {
  const update: ProviderRateUpdate = {};
  if (Number(values.costPerUnit) !== rate.costPerUnit) {
    update.costPerUnit = values.costPerUnit;
  }
  if (values.currency !== rate.currency) {
    update.currency = values.currency;
  }
  if (values.effectiveFrom !== rate.effectiveFrom) {
    update.effectiveFrom = values.effectiveFrom;
  }
  if ((values.effectiveTo || null) !== rate.effectiveTo) {
    update.effectiveTo = values.effectiveTo || null;
  }
  if ((values.notes.trim() || null) !== rate.notes) {
    update.notes = values.notes.trim() || null;
  }
  return update;
}

/**
 * Add a hand-entered rate, or correct one. A correction changes what the rate
 * always was; a price that changes from a date is a new rate starting that
 * day, which closes the open one before it.
 */
export function ProviderRateModal({
  opened,
  onClose,
  rate,
  onCreate,
  onUpdate,
}: Readonly<ProviderRateModalProps>) {
  const editing = Boolean(rate);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<RateFormValues>({
    initialValues: initialValues(rate),
    validate: {
      provider: (v) => (v ? null : 'Choose a provider'),
      service: (v) => (v.trim() ? null : 'Enter the service, as usage records it'),
      unitKey: (v) => (v.trim() ? null : 'Enter the unit, as usage records it'),
      costPerUnit: (v) => (isDecimal(v) ? null : 'A cost such as 0.25, up to six decimal places'),
      effectiveFrom: (v) => (v ? null : 'Choose a start date'),
      effectiveTo: (v, values) => (v && v < values.effectiveFrom ? 'Ends before it starts' : null),
    },
  });

  useEffect(() => {
    if (opened) {
      form.setValues(initialValues(rate));
      form.resetDirty();
      setSubmitError(null);
    }
    // Reset only when the modal opens or the rate being edited changes.
  }, [opened, rate]);

  const submit = form.onSubmit(async (values) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (rate) {
        const update = changedFields(rate, values);
        if (Object.keys(update).length > 0) {
          await onUpdate(rate.id, update);
        }
      } else {
        await onCreate({
          provider: values.provider as ExternalProvider,
          service: values.service,
          unitKey: values.unitKey,
          costPerUnit: values.costPerUnit,
          currency: values.currency,
          effectiveFrom: values.effectiveFrom,
          effectiveTo: values.effectiveTo || null,
          notes: values.notes || null,
        });
      }
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Could not save the rate');
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={editing ? 'Correct rate' : 'Add rate'}
      size="lg"
    >
      <form onSubmit={submit}>
        <Stack>
          <Text size="sm" c="dimmed">
            {editing
              ? 'A correction changes what this rate always was. For a price that changes from a date, add a new rate starting that day instead.'
              : 'Adding a rate that starts after an open-ended one for the same unit closes that one the day before.'}
          </Text>
          <Select
            label="Provider"
            data={EXTERNAL_PROVIDERS.map((p) => ({ value: p, label: providerLabel(p) }))}
            disabled={editing}
            {...form.getInputProps('provider')}
          />
          <Group grow>
            <TextInput
              label="Service"
              placeholder="screen"
              disabled={editing}
              {...form.getInputProps('service')}
            />
            <TextInput
              label="Unit"
              placeholder="checks"
              disabled={editing}
              {...form.getInputProps('unitKey')}
            />
          </Group>
          <Group grow>
            <TextInput
              label="Cost per unit"
              placeholder="0.25"
              inputMode="decimal"
              {...form.getInputProps('costPerUnit')}
            />
            <Select
              label="Currency"
              data={[...COST_CURRENCIES]}
              allowDeselect={false}
              {...form.getInputProps('currency')}
            />
          </Group>
          <Group grow>
            <TextInput type="date" label="From" {...form.getInputProps('effectiveFrom')} />
            <TextInput
              type="date"
              label="To"
              description="Leave empty while it still applies"
              {...form.getInputProps('effectiveTo')}
            />
          </Group>
          <Textarea label="Notes" autosize minRows={2} {...form.getInputProps('notes')} />
          {submitError ? (
            <Alert color="red" title="Not saved">
              {submitError}
            </Alert>
          ) : null}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting}>
              {editing ? 'Save correction' : 'Add rate'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
